/**
 * @license Copyright (c) 2003-2018, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md.
 */
import 'ckeditor5/ckeditor5.css';
import 'ckeditor5-premium-features/ckeditor5-premium-features.css';

import {
	AdjacentListsSupport,
	Alignment,
	Autoformat,
	BlockQuote,
	Bold,
	CKFinder,
	CKFinderUploadAdapter,
	CloudServices,
	DecoupledEditor,
	EasyImage,
	EditorWatchdog,
	Emoji,
	Essentials,
	List,
	ListProperties,
	Font,
	GeneralHtmlSupport,
	Heading,
	HorizontalLine,
	Image,
	ImageCaption,
	ImageResize,
	ImageStyle,
	ImageToolbar,
	ImageUpload,
	Indent,
	IndentBlock,
	Italic,
	Link,
	Mention,
	PageBreak,
	Paragraph,
	PasteFromOffice,
	SimpleUploadAdapter,
	SpecialCharacters,
	SpecialCharactersEssentials,
	Strikethrough,
	Table,
	TableCellProperties,
	TableProperties,
	TableToolbar,
	TextTransformation,
	Underline,
	Fullscreen,
} from 'ckeditor5';

import {
	AIAssistant,
	CaseChange,
	Comments,
	DocumentOutline,
	ExportPdf,
	ExportWord,
	FormatPainter,
	ImportWord,
	MultiLevelList,
	OpenAITextAdapter,
	Pagination,
	PasteFromOfficeEnhanced,
	RealTimeCollaborativeComments,
	RealTimeCollaborativeEditing,
	RealTimeCollaborativeTrackChanges,
	PresenceList,
	RealTimeCollaborativeRevisionHistory,
	RevisionHistory,
	TableOfContents,
	Template,
	TrackChanges,
	TrackChangesPreview
} from 'ckeditor5-premium-features';

import { generateCommentsAndChanges } from './generate-initial-data';

import {
	EXPORT_HORIZONTAL_SPACE,
	EXPORT_VERTICAL_SPACE,
	CS_CONFIG,
	TEMPLATE_DEFINITIONS,
	LICENSE_KEY,
	HTML_SUPPORT_CONFIG,
	IMPORT_CONFIG,
	OPENAI_AUTHORIZATION,
} from './common';

class RevisionEditor extends DecoupledEditor {}
class RevisionEditorRTC extends DecoupledEditor {}

RevisionEditorRTC.builtinPlugins = [
	AdjacentListsSupport,
	AIAssistant,
	OpenAITextAdapter,
	CloudServices,
	Emoji,
	Essentials,
	Alignment,
	Autoformat,
	BlockQuote,
	Bold,
	Comments,
	CaseChange,
	CKFinder,
	CKFinderUploadAdapter,
	TrackChanges,
	RealTimeCollaborativeComments,
	RealTimeCollaborativeEditing,
	RealTimeCollaborativeTrackChanges,
	List,
	ListProperties,
	EasyImage,
	ExportPdf,
	ExportWord,
	Font,
	Fullscreen,
	GeneralHtmlSupport,
	Heading,
	HorizontalLine,
	Image,
	ImageCaption,
	ImageResize,
	ImageStyle,
	ImageToolbar,
	ImageUpload,
	ImportWord,
	Indent,
	IndentBlock,
	Italic,
	Link,
	Mention,
	MultiLevelList,
	PageBreak,
	Paragraph,
	PasteFromOffice,
	SpecialCharacters,
	SpecialCharactersEssentials,
	Strikethrough,
	Table,
	TableCellProperties,
	TableProperties,
	TableToolbar,
	TextTransformation,
	Underline,
	SimpleUploadAdapter,
	Pagination,
	PasteFromOffice,
	PasteFromOfficeEnhanced,
	PresenceList,
	RealTimeCollaborativeRevisionHistory,
	RevisionHistory,
	Template,
	FormatPainter,
	TableOfContents,
	DocumentOutline,
	TrackChangesPreview
];

RevisionEditorRTC.defaultConfig = {
	cloudServices: CS_CONFIG,
	toolbar: {
		items: [
			'aiCommands',
			'aiAssistant',
			'|',
			'revisionHistory',
			'trackChanges',
			'comment',
			'commentsArchive',
			'|',
			'exportPdf',
			'exportWord',
			'importWord',
			'|',
			'previousPage',
			'nextPage',
			'pageNavigation',
			'pageBreak',
			'|',
			'link',
			'blockquote',
			'uploadImage',
			'insertTable',
			'specialCharacters',
			'horizontalLine',
			'emoji',
			'|',
			'multiLevelList',
			'numberedList',
			'bulletedList',
			'|',
			'indent',
			'outdent',
			'|',
			'undo',
			'redo',
			'-',
			'heading',
			'|',
			'fontSize',
			'fontFamily',
			'fontColor',
			'fontBackgroundColor',
			'|',
			'bold',
			'italic',
			'underline',
			'strikethrough',
			'|',
			'alignment',
			'|',
			'insertTemplate',
			'tableOfContents',
			'|',
			'formatPainter',
			'caseChange',
			'|',
			'fullscreen',
		],
		shouldNotGroupWhenFull: true,
	},
	emoji: {
		definitionsUrl: 'cdn'
	},
	image: {
		toolbar: [
			'toggleImageCaption',
			'imageStyle:inline',
			'imageStyle:wrapText',
			'imageStyle:breakText',
		],
		styles: ['inline', 'block', 'side'],
	},
	table: {
		contentToolbar: [
			'tableColumn',
			'tableRow',
			'mergeTableCells',
			'tableProperties',
			'tableCellProperties',
		],
	},
	indentBlock: {
		offset: 1,
		unit: 'em',
	},
	language: 'en',
	fontFamily: {
		supportAllValues: true,
	},
	fontSize: {
		options: [10, 12, 14, 'default', 18, 20, 22],
		supportAllValues: true,
	},
	list: {
		properties: {
			styles: true,
			startIndex: true,
			reversed: true,
		},
	},
	pagination: {
		// A4
		pageWidth: '21cm',
		pageHeight: '29.7cm',

		pageMargins: {
			top: EXPORT_VERTICAL_SPACE,
			bottom: EXPORT_VERTICAL_SPACE,
			right: EXPORT_HORIZONTAL_SPACE,
			left: EXPORT_HORIZONTAL_SPACE,
		},
	},
	htmlSupport: HTML_SUPPORT_CONFIG,
	exportPdf: {
		stylesheets: ['EDITOR_STYLES', '/assets/styles/export-demo-cke5.css'],
		appID: 'cke5-manual-demo',
		// converterUrl: 'https://pdf-converter.cke-cs-dev.com/v1/convert',
		converterOptions: {
			format: 'A4',
			margin_top: EXPORT_VERTICAL_SPACE,
			margin_bottom: EXPORT_VERTICAL_SPACE,
			margin_right: EXPORT_HORIZONTAL_SPACE,
			margin_left: EXPORT_HORIZONTAL_SPACE,
			page_orientation: 'portrait',
		},
		dataCallback: (editor) =>
			editor.getData({
				showSuggestionHighlights: true,
				showCommentHighlights: true,
			}),
		tokenUrl: false,
	},
	exportWord: {
		version: 2,
		stylesheets: ['EDITOR_STYLES', '/assets/styles/export-word-cke5.css'],
		fileName: 'my-file.docx',
		converterOptions: {
			document: {
				size: 'A4', // Default value, you don't need to specify it explicitly for A4.
				margins: {
					top: EXPORT_VERTICAL_SPACE,
					bottom: EXPORT_VERTICAL_SPACE,
					right: EXPORT_HORIZONTAL_SPACE,
					left: EXPORT_HORIZONTAL_SPACE,
				},
			},
		},
		tokenUrl: false,
	},
	importWord: {
		tokenUrl: IMPORT_CONFIG.tokenUrl,
		converterUrl: IMPORT_CONFIG.converterUrl,
		formatting: {
			resets: 'none',
			defaults: 'inline',
			styles: 'inline',
			comments: 'basic',
		},
		collaborationFeatures: {
			userId: 'tester',
			comments: true,
			trackChanges: true,
		},
	},
	trackChanges: {
		preview: {
			renderFunction: ( container, elements ) => {
				container.classList.add( 'formatted' );

				for ( const element of elements ) {
					container.appendChild( element );
				}
			}
		}
	},
	comments: {
		editorConfig: {
			extraPlugins: [Autoformat, Bold, Italic, List, Mention],
		},
	},
	licenseKey: LICENSE_KEY,
	template: {
		definitions: TEMPLATE_DEFINITIONS,
	},
	documentOutline: {
		container: document.querySelector('.demo-collaboration-rtc__outline'),
	},
	ai: {
		openAI: {
			apiUrl: 'https://openai.public-demo.proxy.ai.cke-cs.com/v1/chat/completions',
			requestHeaders: {
				// eslint-disable-next-line max-len
				Authorization: OPENAI_AUTHORIZATION,
			},
			requestParameters: {
				model: 'gpt-4o',
				max_tokens: 4000,
			},
		},
		aiAssistant: {
			contentAreaCssClass: 'formatted',
		},
	},
	caseChange: {
		titleCase: {
			excludeWords: [
				'a',
				'an',
				'and',
				'as',
				'at',
				'but',
				'by',
				'en',
				'for',
				'if',
				'in',
				'nor',
				'of',
				'on',
				'or',
				'per',
				'the',
				'to',
				'vs',
				'vs.',
				'via',
			],
		},
	},
};

RevisionEditor.builtinPlugins = [
	AdjacentListsSupport,
	AIAssistant,
	OpenAITextAdapter,
	CloudServices,
	Essentials,
	Alignment,
	Autoformat,
	BlockQuote,
	Bold,
	Comments,
	CaseChange,
	CKFinder,
	CKFinderUploadAdapter,
	List,
	ListProperties,
	TrackChanges,
	EasyImage,
	Emoji,
	ExportPdf,
	ExportWord,
	Font,
	Fullscreen,
	GeneralHtmlSupport,
	Heading,
	HorizontalLine,
	Image,
	ImageCaption,
	ImageResize,
	ImageStyle,
	ImageToolbar,
	ImageUpload,
	Indent,
	IndentBlock,
	ImportWord,
	Italic,
	Link,
	Mention,
	MultiLevelList,
	PageBreak,
	Paragraph,
	PasteFromOffice,
	PasteFromOfficeEnhanced,
	SpecialCharacters,
	SpecialCharactersEssentials,
	Strikethrough,
	Table,
	TableCellProperties,
	TableProperties,
	TableToolbar,
	TextTransformation,
	Underline,
	SimpleUploadAdapter,
	Pagination,
	RevisionHistory,
	Template,
	FormatPainter,
	TableOfContents,
	DocumentOutline,
	TrackChangesPreview
];

RevisionEditor.defaultConfig = {
	cloudServices: CS_CONFIG,
	toolbar: {
		items: [
			'aiCommands',
			'aiAssistant',
			'|',
			'revisionHistory',
			'trackChanges',
			'comment',
			'commentsArchive',
			'|',
			'exportPdf',
			'exportWord',
			'importWord',
			'|',
			'previousPage',
			'nextPage',
			'pageNavigation',
			'pageBreak',
			'|',
			'link',
			'blockquote',
			'uploadImage',
			'insertTable',
			'emoji',
			'specialCharacters',
			'horizontalLine',
			'|',
			'multiLevelList',
			'numberedList',
			'bulletedList',
			'|',
			'indent',
			'outdent',
			'|',
			'undo',
			'redo',
			'-',
			'heading',
			'|',
			'fontSize',
			'fontFamily',
			'fontColor',
			'fontBackgroundColor',
			'|',
			'bold',
			'italic',
			'underline',
			'strikethrough',
			'|',
			'alignment',
			'|',
			'insertTemplate',
			'tableOfContents',
			'|',
			'formatPainter',
			'caseChange',
			'|',
			'fullscreen',
		],
		shouldNotGroupWhenFull: true,
	},
	emoji: {
		definitionsUrl: 'cdn'
	},
	image: {
		toolbar: [
			'toggleImageCaption',
			'imageStyle:inline',
			'imageStyle:wrapText',
			'imageStyle:breakText',
		],
		styles: ['inline', 'block', 'side'],
	},
	table: {
		contentToolbar: [
			'tableColumn',
			'tableRow',
			'mergeTableCells',
			'tableProperties',
			'tableCellProperties',
		],
	},
	indentBlock: {
		offset: 1,
		unit: 'em',
	},
	language: 'en',
	fontFamily: {
		supportAllValues: true,
	},
	fontSize: {
		options: [10, 12, 14, 'default', 18, 20, 22],
		supportAllValues: true,
	},
	list: {
		properties: {
			styles: true,
			startIndex: true,
			reversed: true,
		},
	},
	pagination: {
		// A4
		pageWidth: '21cm',
		pageHeight: '29.7cm',

		pageMargins: {
			top: EXPORT_VERTICAL_SPACE,
			bottom: EXPORT_VERTICAL_SPACE,
			right: EXPORT_HORIZONTAL_SPACE,
			left: EXPORT_HORIZONTAL_SPACE,
		},
	},
	htmlSupport: HTML_SUPPORT_CONFIG,
	exportPdf: {
		stylesheets: ['EDITOR_STYLES', '/assets/styles/export-demo-cke5.css'],
		appID: 'cke5-manual-demo',
		// converterUrl: 'https://pdf-converter.cke-cs-dev.com/v1/convert',
		converterOptions: {
			format: 'A4',
			margin_top: EXPORT_VERTICAL_SPACE,
			margin_bottom: EXPORT_VERTICAL_SPACE,
			margin_right: EXPORT_HORIZONTAL_SPACE,
			margin_left: EXPORT_HORIZONTAL_SPACE,
			page_orientation: 'portrait',
		},
		dataCallback: (editor) =>
			editor.getData({
				showSuggestionHighlights: true,
				showCommentHighlights: true,
			}),
		tokenUrl: false,
	},
	exportWord: {
		version: 2,
		stylesheets: ['EDITOR_STYLES', '/assets/styles/export-word-cke5.css'],
		fileName: 'my-file.docx',
		converterOptions: {
			format: 'A4', // Default value, you don't need to specify it explicitly for A4.
			margin_top: EXPORT_VERTICAL_SPACE,
			margin_bottom: EXPORT_VERTICAL_SPACE,
			margin_right: EXPORT_HORIZONTAL_SPACE,
			margin_left: EXPORT_HORIZONTAL_SPACE,
		},
		tokenUrl: false,
	},
	importWord: {
		tokenUrl: IMPORT_CONFIG.tokenUrl,
		converterUrl: IMPORT_CONFIG.converterUrl,
		formatting: {
			resets: 'none',
			defaults: 'inline',
			styles: 'inline',
			comments: 'basic',
		},
		collaborationFeatures: {
			userId: 'tester',
			comments: true,
			trackChanges: true,
		},
	},
	trackChanges: {
		preview: {
			renderFunction: ( container, elements ) => {
				container.classList.add( 'formatted' );

				for ( const element of elements ) {
					container.appendChild( element );
				}
			}
		}
	},
	comments: {
		editorConfig: {
			extraPlugins: [Autoformat, Bold, Italic, List, Mention],
		},
	},
	licenseKey: LICENSE_KEY,
	template: {
		definitions: TEMPLATE_DEFINITIONS,
	},
	documentOutline: {
		container: document.querySelector('.demo-collaboration__outline'),
	},
	ai: {
		openAI: {
			apiUrl: 'https://openai.public-demo.proxy.ai.cke-cs.com/v1/chat/completions',
			requestHeaders: {
				// eslint-disable-next-line max-len
				Authorization: OPENAI_AUTHORIZATION,
			},
			requestParameters: {
				model: 'gpt-4o',
				max_tokens: 4000,
			},
		},
		aiAssistant: {
			contentAreaCssClass: 'formatted',
		},
	},
	caseChange: {
		titleCase: {
			excludeWords: [
				'a',
				'an',
				'and',
				'as',
				'at',
				'but',
				'by',
				'en',
				'for',
				'if',
				'in',
				'nor',
				'of',
				'on',
				'or',
				'per',
				'the',
				'to',
				'vs',
				'vs.',
				'via',
			],
		},
	},
};

export default {
	RevisionHistoryRTC: RevisionEditorRTC,
	RevisionHistory: RevisionEditor,
	generateCommentsAndChanges: generateCommentsAndChanges,
	EditorWatchdog,
};
